import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "../ui";
import { Modal, Nav, Form, Badge, CloseButton } from "react-bootstrap";
import { MDBSpinner, MDBInput, MDBTooltip } from "mdb-react-ui-kit";
import { ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ar as arFns, enUS as enUSFns } from "date-fns/locale";
import { api } from "../../services";
import { toast } from "react-toastify";
import { normalizeRestaurantId } from "../../utils/utils";
import { getAllBranchIds } from "../../utils/userScope";
import { usePermission } from "../../hooks/usePermission";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../Modals/ConfirmModal";
import PermissionAccordion from "./PermissionAccordion";
import { ONEHUB_BRAND_FORM_CONTROLS_CSS } from "./brandFormControlsCss";
import { translateRbacApiMessage } from "../../utils/translateRbacApiMessage";
import {
  reconcileUsersMutateRequiresRolesView,
  ROLES_VIEW_PERM,
  ROLES_CREATE_PERM,
  USERS_MUTATE_PERMS,
  permissionDomainIdContainingKey,
} from "../../utils/reconcileUsersMutateRoleDeps";
import { finalizeRolePermissions } from "../../utils/normalizeRetiredRolePermissions";

/** Modal palette aligned with role-create / assigned-users mock (Figma). */
const BRAND = "#083B4C";
const ROLE_UI_BORDER = "#e0e0e0";
const ROLE_UI_DIVIDER = "#D1D1D1";
const ROLE_UI_SECTION_HEADING = "#303030";
const ROLE_UI_MUTED = "#6b7280";
const ROLE_UI_TITLE = "#111827";
const ROLE_UI_LABEL = "#374151";
const ROLE_UI_INPUT_TEXT = "#111827";
const ROLE_AVATAR_BG = "#FFF4EC";
const ROLE_AVATAR_FG = "#FF9B53";

/** Delete-role panel — colors from design SVG (#E21D1D stroke/title/button text, #E84A4A body + button outline). */
const ROLE_DELETE_PRIMARY = "#E21D1D";
const ROLE_DELETE_SECONDARY = "#E84A4A";

/** Shell + PermissionAccordion tweaks scoped under `.role-wizard-modal`. */
const ROLE_WIZARD_MODAL_STYLES = `
.role-wizard-modal .modal-dialog {
  max-width: 686px;
}
.role-wizard-modal .modal-content {
  border: none !important;
  border-radius: 10px !important;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08) !important;
  overflow: hidden;
  background: #ffffff !important;
}
.role-wizard-modal .modal-header {
  border-bottom: none !important;
  padding: 16px 24px !important;
}
.role-wizard-modal .modal-header .modal-title {
  font-family: Inter, Assistant, sans-serif;
  font-size: 16px !important;
  font-weight: 700 !important;
  line-height: 150%;
  letter-spacing: 0;
  color: #303030 !important;
}
.role-wizard-modal .role-wizard-user-count-badge {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  height: 15px;
  padding: 0 8px !important;
  background: #e3e9eb !important;
  border: 1px solid #92a8af !important;
  border-radius: 20px !important;
  color: #303030 !important;
  font-family: Inter, Assistant, sans-serif;
  font-size: 10px !important;
  font-weight: 400 !important;
  line-height: 150%;
  white-space: nowrap;
}
.role-wizard-modal .modal-header .role-wizard-subtitle {
  color: #474747 !important;
  font-family: Inter, Assistant, sans-serif;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 150%;
  margin-top: 0 !important;
}
.role-wizard-modal .modal-body {
  padding: 0 24px 20px !important;
}
.role-wizard-modal .modal-body.onehub-brand-form-controls {
  --color-primary: ${BRAND};
  --oh-brand-form: ${BRAND};
  --oh-brand-form-focus: rgba(8, 59, 76, 0.28);
}
.role-wizard-modal .modal-footer {
  border-top: 1px solid ${ROLE_UI_DIVIDER} !important;
  padding: 20px 24px !important;
}
.role-wizard-modal .role-wizard-section-label {
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${ROLE_UI_SECTION_HEADING};
  text-transform: uppercase;
  margin-bottom: 10px;
}
.role-wizard-modal .role-wizard-field-label {
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${ROLE_UI_LABEL};
  margin-bottom: 4px;
}
.role-wizard-modal .role-wizard-field-error {
  font-size: 0.8125rem;
  line-height: 1.35;
  margin: 4px 0 0;
  min-height: 0;
}
.role-wizard-modal .role-wizard-description-label {
  margin-top: 8px !important;
}
.role-wizard-modal .role-wizard-input.form-control {
  border: 1px solid ${ROLE_UI_BORDER};
  border-radius: 4px;
  min-height: 40px;
  padding: 8px 12px;
  font-size: 0.875rem;
  color: ${ROLE_UI_INPUT_TEXT};
}
.role-wizard-modal .role-wizard-input.form-control:focus {
  border-color: ${BRAND};
  box-shadow: 0 0 0 0.2rem rgba(0, 61, 77, 0.18);
}
.role-wizard-modal .role-wizard-input.form-control::placeholder {
  color: #9ca3af;
}
.role-wizard-modal .role-wizard-details-block {
  margin-top: 16px;
  margin-bottom: 1.25rem;
}
.role-wizard-modal .role-wizard-permissions-block {
  margin-top: 0.2rem;
}
/* PermissionAccordion — role wizard: black permissions heading, compact rows, design gap after toggles. */
.role-wizard-modal .permission-accordion-figma .permission-accordion-toolbar {
  margin-bottom: 0.45rem !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-toolbar span.fw-bold.small {
  color: #000000 !important;
  font-size: 11px !important;
  letter-spacing: 0.08em !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-expand,
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-expand.btn {
  text-transform: none !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-expand .fw-semibold {
  color: ${ROLE_UI_TITLE} !important;
  line-height: 1.3 !important;
  text-transform: none !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-expand svg {
  width: 17px !important;
  height: 17px !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-toolbar label[for="perm-select-all"] {
  color: ${ROLE_UI_LABEL} !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-shell {
  border-color: ${ROLE_UI_BORDER} !important;
  border-radius: 4px !important;
  /* Switch column fits ~33px slider; gap to label is section-head column-gap below. */
  --perm-control-col: 2.25rem;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-section-head {
  min-height: 40px !important;
  column-gap: 0.75rem !important;
  padding-top: 0.3125rem !important;
  padding-bottom: 0.3125rem !important;
  padding-left: 0.625rem !important;
  padding-right: 0.625rem !important;
}
.role-wizard-modal .role-wizard-permissions-block .permission-accordion-figma > .d-flex.flex-column.gap-2 {
  gap: 0.3125rem !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-details {
  padding-block: 0.45rem 0.55rem !important;
  padding-inline: 0.6875rem !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-details-rows {
  gap: 0.3125rem !important;
}
.role-wizard-modal .permission-accordion-figma .perm-detail-row {
  column-gap: 0.75rem !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-summary {
  margin-inline: 0 !important;
  margin-top: 0.45rem !important;
  padding-block: 0.45rem !important;
  padding-inline: 0.6875rem !important;
  font-size: 0.8125rem !important;
  line-height: 1.35 !important;
}
.role-wizard-modal .permission-accordion-figma .permission-accordion-summary-dot {
  width: 7px !important;
  height: 7px !important;
}
.role-wizard-modal .permission-accordion-figma .perm-detail-label-col {
  font-size: 0.8125rem !important;
  line-height: 1.32 !important;
}
.role-wizard-modal .btn-role-wizard-cancel {
  border: 1px solid ${BRAND} !important;
  color: ${BRAND} !important;
  background-color: #fff !important;
  border-radius: 4px !important;
  padding: 6px 12px !important;
  min-height: 27px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  line-height: 150% !important;
}
.role-wizard-modal .btn-role-wizard-cancel:hover:not(:disabled) {
  background-color: rgba(8, 59, 76, 0.06) !important;
  color: ${BRAND} !important;
}
.role-wizard-modal .btn-role-wizard-primary {
  background-color: ${BRAND} !important;
  border-color: ${BRAND} !important;
  color: #fff !important;
  border-radius: 4px !important;
  padding: 6px 12px !important;
  min-height: 27px !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  line-height: 150% !important;
}
.role-wizard-modal .btn-role-wizard-primary:disabled {
  opacity: 0.55;
}
.role-wizard-modal .role-wizard-delete-role-panel {
  margin-top: 1.5rem;
  padding: 14px 16px;
  border: 0.5px solid ${ROLE_DELETE_PRIMARY};
  border-radius: 4px;
  background: #fff;
}
.role-wizard-modal .role-wizard-delete-role-title {
  font-family: Inter, sans-serif;
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${ROLE_DELETE_PRIMARY};
  line-height: 1.35;
  margin-bottom: 4px;
}
.role-wizard-modal .role-wizard-delete-role-desc {
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 400;
  color: ${ROLE_DELETE_SECONDARY};
  line-height: 1.45;
  margin: 0;
}
.role-wizard-modal .btn-role-wizard-delete-outline {
  border: 0.5px solid ${ROLE_DELETE_SECONDARY} !important;
  color: ${ROLE_DELETE_PRIMARY} !important;
  background-color: #fff !important;
  border-radius: 4px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 0.8125rem !important;
}
.role-wizard-modal .btn-role-wizard-delete-outline:hover:not(:disabled) {
  background-color: rgba(226, 29, 29, 0.06) !important;
  border-color: ${ROLE_DELETE_SECONDARY} !important;
  color: ${ROLE_DELETE_PRIMARY} !important;
}
`;

/** Edit-role only: two separate tab buttons (#E8E8E8 active / #F2F2F2 idle) — Figma 1:8168. */
const ROLE_WIZARD_NAV_STYLES = `
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented {
  display: flex !important;
  flex-wrap: nowrap;
  justify-content: flex-start !important;
  align-items: center;
  gap: 6px !important;
  width: calc(100% + 48px);
  max-width: none;
  margin: 0 -24px 0 !important;
  padding: 0 24px 20px !important;
  border: none !important;
  border-bottom: 0.5px solid #e8e8e8 !important;
}
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented .nav-item {
  flex: 0 0 auto !important;
  margin: 0 !important;
}
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented .role-wizard-nav-link.nav-link {
  width: auto;
  height: 32px;
  min-height: 32px;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 4px 12px !important;
  margin: 0 !important;
  border-radius: 8px !important;
  border: 1px solid #bababa !important;
  font-family: Inter, Assistant, sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 150% !important;
  color: #030f1b !important;
  background-color: #f2f2f2 !important;
  text-decoration: none !important;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  position: relative;
}
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented .role-wizard-nav-link.nav-link:hover:not(.active) {
  background-color: #ececec !important;
}
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented .role-wizard-nav-link.nav-link:focus-visible {
  outline: none !important;
  z-index: 1 !important;
  box-shadow: 0 0 0 2px rgba(8, 59, 76, 0.35) !important;
}
.role-wizard-modal .role-wizard-nav.role-wizard-nav-segmented .role-wizard-nav-link.nav-link.active {
  background-color: #e8e8e8 !important;
  border-color: #bababa !important;
  color: #030f1b !important;
  font-weight: 700 !important;
  box-shadow: none !important;
}

.role-wizard-modal .role-wizard-assigned-users {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  padding-top: 10px;
  width: 100%;
}
.role-wizard-modal .role-wizard-assigned-users__head {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 26px;
}
.role-wizard-modal .role-wizard-assigned-users__label {
  font-family: Inter, Assistant, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
  text-transform: uppercase;
  color: #303030;
  margin: 0;
}
.role-wizard-modal .role-wizard-assign-user-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  padding: 4px 8px;
  border: 0.5px solid #d1d1d1;
  border-radius: 2px;
  background: #ffffff;
  color: #303030;
  font-family: Inter, Assistant, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  cursor: pointer;
}
.role-wizard-modal .role-wizard-assign-user-btn:hover:not(:disabled) {
  background: #f7f7f7;
}
.role-wizard-modal .role-wizard-assign-user-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.role-wizard-modal .role-wizard-assign-user-btn__plus {
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
}
.role-wizard-modal .role-wizard-assigned-users__list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  width: 100%;
  border: 0.5px solid #d1d1d1;
  border-radius: 2px;
  overflow: hidden;
  background: #ffffff;
}
.role-wizard-modal .role-wizard-assigned-user-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 54px;
  padding: 6px 12px;
  border-bottom: 0.5px solid #d1d1d1;
  box-sizing: border-box;
}
.role-wizard-modal .role-wizard-assigned-user-row:last-child {
  border-bottom: none;
}
.role-wizard-modal .role-wizard-assigned-user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 20px;
  background: ${ROLE_AVATAR_BG};
  color: ${ROLE_AVATAR_FG};
  font-family: Inter, Assistant, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
}
.role-wizard-modal .role-wizard-assigned-user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1 1 auto;
}
.role-wizard-modal .role-wizard-assigned-user-name {
  font-family: Inter, Assistant, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 150%;
  color: #030f1b;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.role-wizard-modal .role-wizard-assigned-user-username {
  font-family: Inter, Assistant, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #030f1b;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.role-wizard-modal .role-wizard-assigned-users__empty {
  margin: 0;
  padding: 16px 12px;
  font-size: 12px;
  color: ${ROLE_UI_MUTED};
}
`;

function isImmutableAdminRoleName(roleName) {
  return String(roleName || "").trim().toLowerCase() === "admin";
}

/** Group assignment rows by staff user (same role may exist per store). */
function groupAssignmentsByUserId(rows) {
  const m = new Map();
  for (const row of rows || []) {
    const uid = String(row.userId?._id || row.userId || "");
    if (!uid) continue;
    if (!m.has(uid)) m.set(uid, []);
    m.get(uid).push(row);
  }
  return m;
}

function initials(userObj) {
  const first = String(userObj?.firstName || "").trim();
  const last = String(userObj?.lastName || "").trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  const s = String(userObj?.username || userObj?.email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase() || "?";
}

function displayFullName(userObj, fallbackLabel) {
  const first = String(userObj?.firstName || "").trim();
  const last = String(userObj?.lastName || "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  return String(userObj?.username || fallbackLabel || "").trim() || fallbackLabel;
}

/**
 * Create-role flow and edit-role flow with tabs (details / assigned users) per design mocks.
 */
export default function RoleWizardModal({
  show,
  onHide,
  restaurantId,
  roleId,
  initialUserCount = 0,
  onSaved,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const { t, i18n } = useTranslation("translation");
  const uiDir = i18n.dir();
  const { bypass } = usePermission();
  const rid = normalizeRestaurantId(restaurantId);
  const isEdit = Boolean(roleId);
  const canSave = isEdit ? canUpdate : canCreate;
  const [activeTab, setActiveTab] = useState("details");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [registry, setRegistry] = useState({});
  const [permDomains, setPermDomains] = useState([]);
  const [expandedMods, setExpandedMods] = useState(() => new Set());

  const [loadingRole, setLoadingRole] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleMeta, setRoleMeta] = useState(null);
  /** Inline validation (aligned with Products create form: `text-danger` + reserved paragraph spacing). */
  const [fieldErrors, setFieldErrors] = useState({});

  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const [pickUserOpen, setPickUserOpen] = useState(false);
  const [assignWizardStep, setAssignWizardStep] = useState(0);
  const [selectedAssignUser, setSelectedAssignUser] = useState(null);
  const [assignStoresList, setAssignStoresList] = useState([]);
  const [assignLoadingStores, setAssignLoadingStores] = useState(false);
  const [assignFullBranchAccess, setAssignFullBranchAccess] = useState(true);
  const [assignSelectedBranchIds, setAssignSelectedBranchIds] = useState([]);
  const [assignBranchSearch, setAssignBranchSearch] = useState("");
  const [assignExpandedStores, setAssignExpandedStores] = useState(() => new Set());
  const [assignStaffSearch, setAssignStaffSearch] = useState("");
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  const [staffCandidates, setStaffCandidates] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState({
    title: "",
    message: "",
    action: async () => {},
  });

  const resetForm = useCallback(() => {
    setActiveTab("details");
    setName("");
    setDescription("");
    setPermissions([]);
    setExpandedMods(new Set());
    setRoleMeta(null);
    setAssignments([]);
    setPickUserOpen(false);
    setAssignWizardStep(0);
    setSelectedAssignUser(null);
    setAssignStoresList([]);
    setAssignFullBranchAccess(true);
    setAssignSelectedBranchIds([]);
    setAssignBranchSearch("");
    setAssignExpandedStores(new Set());
    setAssignStaffSearch("");
    setAssignSubmitting(false);
    setStaffCandidates([]);
    setFieldErrors({});
  }, []);

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }
    setActiveTab("details");
    (async () => {
      try {
        const permRes = await api.get("/v1/permissions-registry");
        const data = permRes.data || {};
        const reg = data.appRouteRegistry || data.registry || {};
        setRegistry(reg);
        setPermDomains(Array.isArray(data.domains) ? data.domains : []);
      } catch {
        toast.error(t("rbac.failedLoadPermissionRegistry"));
      }
    })();
  }, [show, resetForm, t]);

  useEffect(() => {
    if (!show) return;

    if (!roleId) {
      setName("");
      setDescription("");
      setPermissions([]);
      setRoleMeta(null);
      setAssignments([]);
      setFieldErrors({});
      return;
    }

    // Company/admin users can edit roles without specific restaurant context
    if (!rid && !bypass) return;

    setLoadingRole(true);
    setFieldErrors({});
    api
      .get(`/v1/roles/${roleId}`)
      .then((res) => {
        const r = res.data;
        setRoleMeta(r);
        setName(r.name || "");
        setDescription(r.description || "");
        const rawPerms = Array.isArray(r.permissions) ? [...r.permissions] : [];
        const { permissions: normalizedPerms } = reconcileUsersMutateRequiresRolesView(
          rawPerms,
          rawPerms
        );
        setPermissions(finalizeRolePermissions(normalizedPerms));
      })
      .catch((err) => {
        toast.error(translateRbacApiMessage(err.response?.data?.message, t) || t("rbac.failedLoadRole"));
        onHide();
      })
      .finally(() => setLoadingRole(false));
  }, [show, roleId, rid, onHide, t]);

  /** Tenant/store id for assignment APIs: URL scope, then role's home restaurant (store-scoped roles). */
  const assignmentTenantId = useMemo(
    () => rid || normalizeRestaurantId(roleMeta?.restaurantId),
    [rid, roleMeta?.restaurantId]
  );

  const fetchAssignments = useCallback(async () => {
    if (!roleId) return;
    if (!assignmentTenantId && !bypass) return;

    setLoadingAssignments(true);
    try {
      const params = new URLSearchParams();
      params.set("roleId", roleId);
      if (assignmentTenantId) {
        params.set("restaurantId", assignmentTenantId);
      }

      const res = await api.get(`/v1/user-role-assignments?${params}`);
      setAssignments(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t("rbac.failedLoadAssignments"));
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [assignmentTenantId, roleId, bypass, t]);

  useEffect(() => {
    if (show && isEdit && roleId && (rid || bypass)) {
      fetchAssignments();
    }
  }, [show, isEdit, rid, roleId, bypass, fetchAssignments]);

  const toggleExpand = (mod) => {
    setExpandedMods((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
  };

  const assignmentsByUser = useMemo(() => groupAssignmentsByUserId(assignments), [assignments]);
  const uniqueUserCount = assignmentsByUser.size;
  const assignedCount = uniqueUserCount || initialUserCount;

  const isProtectedAdmin = isEdit && isImmutableAdminRoleName(roleMeta?.name);
  const isProtectedDefaultRole = isEdit && roleMeta?.isDefaultRole === true;
  const isProtectedRole = isProtectedAdmin || isProtectedDefaultRole;
  const mutationLocked = isProtectedRole || !canSave;
  const showDetailsSaveButton = isEdit ? canUpdate : canCreate;
  const protectedRoleSaveTip = isProtectedDefaultRole
    ? t("rbac.defaultRoleReadOnly")
    : t("rbac.adminRoleReadOnly");

  const usersMutateWithoutRoleDeps = useMemo(() => {
    const p = new Set(permissions || []);
    const hasMutate = USERS_MUTATE_PERMS.some((k) => p.has(k));
    return (
      hasMutate && (!p.has(ROLES_VIEW_PERM) || !p.has(ROLES_CREATE_PERM))
    );
  }, [permissions]);

  const lastEditedLine = useMemo(() => {
    if (!roleMeta?.updatedAt) return null;
    try {
      const d = new Date(roleMeta.updatedAt);
      const locale = i18n.language?.toLowerCase().startsWith("ar") ? arFns : enUSFns;
      return t("rbac.lastEditedRelative", {
        when: formatDistanceToNow(d, { addSuffix: true, locale }),
      });
    } catch {
      return null;
    }
  }, [roleMeta, t, i18n.language]);

  async function handleSave() {
    if (isEdit ? !canUpdate : !canCreate) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    const duplicateInline = t("rbac.duplicateRoleNotAllowed");
    const trimmed = name.trim();
    const errors = {};
    if (!trimmed) {
      errors.name = t("rbac.roleNameRequired");
    } else if (trimmed.length < 3) {
      errors.name = t("rbac.roleNameMinLength");
    } else if (trimmed.length > 25) {
      errors.name = t("rbac.roleNameMaxLength");
    }

    if (!isProtectedRole) {
      if (!Array.isArray(permissions) || permissions.length === 0) {
        errors.permissions = t("rbac.permissionRequiredForCreate");
      } else if (usersMutateWithoutRoleDeps) {
        errors.permissions = t("rbac.usersMgmtNeedsRolesViewAndCreate");
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      if (isEdit) {
        // Company/admin users can edit roles without restaurant context
        if (!rid && !bypass) {
          toast.error(t("rbac.restaurantContextMissing"));
          return;
        }
        await api.put(`/v1/roles/${roleId}`, {
          name: trimmed,
          description: description.trim(),
          permissions: finalizeRolePermissions(permissions),
        });
        toast.success(t("rbac.roleUpdated"));
      } else {
        const body = {
          name: trimmed,
          description: description.trim(),
          permissions: finalizeRolePermissions(permissions),
        };
        
        // For company/admin users, create account-level role when no specific restaurantId
        // For single restaurant contexts, create restaurant-specific role  
        if (rid && !bypass) {
          // Non-company user with specific restaurant
          body.restaurantId = rid;
          console.log(`[RoleWizardModal] Creating restaurant-specific role for restaurantId: ${rid}`);
        } else if (bypass) {
          // Company/admin user - create account-level role
          body.accountLevel = true;
          console.log('[RoleWizardModal] Creating account-level role for company/admin user');
        } else {
          // No restaurant context and not company user - this shouldn't happen
          toast.error(t("rbac.restaurantContextMissing"));
          return;
        }
        const res = await api.post("/v1/roles", body);
        if (res.data?.count != null && res.data.count > 1) {
          toast.success(`${t("rbac.roleCreated")} (${res.data.count})`);
        } else {
          toast.success(t("rbac.roleCreated"));
        }
      }
      closeAssignModal();
      onSaved?.();
      onHide();
    } catch (err) {
      const status = err.response?.status;
      const apiRaw = err.response?.data?.message;
      const apiMsg = apiRaw ? translateRbacApiMessage(apiRaw, t) : "";
      const generic = t("rbac.genericServerError");

      if (status === 409) {
        setFieldErrors((prev) => ({
          ...prev,
          name: duplicateInline,
        }));
        return;
      }
      if (status === 403 && apiRaw) {
        toast.error(apiMsg || generic);
        return;
      }
      if (status === 400 && apiRaw) {
        const lower = String(apiRaw).toLowerCase();
        if (lower.includes("permission")) {
          setFieldErrors((prev) => ({
            ...prev,
            permissions: apiMsg,
          }));
        } else {
          setFieldErrors((prev) => ({
            ...prev,
            name: apiMsg,
          }));
        }
        return;
      }
      if (!err.response || status >= 500) {
        toast.error(generic);
        return;
      }
      toast.error(apiMsg || t("rbac.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  function openDeleteRoleConfirm() {
    if (!canDelete) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    const n = uniqueUserCount || initialUserCount;
    const warn =
      n > 0
        ? ` ${t("rbac.deleteRoleWarningCount", { count: n })}`
        : "";
    setConfirmData({
      title: t("rbac.deleteRoleTitle"),
      message: `${t("rbac.deleteRoleQuestion")}${warn}`,
      action: async () => {
        try {
          await api.delete(`/v1/roles/${roleId}`);
          toast.success(t("rbac.roleDeleted"));
          closeAssignModal();
          onSaved?.();
          onHide();
        } catch (err) {
          const raw = err.response?.data?.message;
          const msg = raw ? translateRbacApiMessage(raw, t) : t("rbac.deleteFailed");
          const c = err.response?.data?.assignmentCount;
          toast.error(c != null ? `${msg} (${c})` : msg);
        }
      },
    });
    setShowConfirm(true);
  }

  function openRemoveAssignmentConfirm(rowsForUser) {
    if (!canUpdate) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    const list = Array.isArray(rowsForUser) ? rowsForUser : [rowsForUser];
    const first = list[0];
    const u = first?.userId;
    const uname = typeof u === "object" && u ? u.username : t("rbac.userLabel");
    const multi = list.length > 1;
    setConfirmData({
      title: t("rbac.removeUserFromRoleTitle"),
      message: multi
        ? t("rbac.removeUserConfirmMulti", { name: uname, count: list.length })
        : t("rbac.removeUserConfirmSingle", { name: uname }),
      action: async () => {
        try {
          await Promise.all(list.map((row) => api.delete(`/v1/user-role-assignments/${row._id}`)));
          toast.success(t("rbac.userRemovedFromRole"));
          fetchAssignments();
          onSaved?.();
        } catch (err) {
          toast.error(translateRbacApiMessage(err.response?.data?.message, t) || t("rbac.removeFailed"));
        }
      },
    });
    setShowConfirm(true);
  }

  function resetAssignWizard() {
    setAssignWizardStep(0);
    setSelectedAssignUser(null);
    setAssignStoresList([]);
    setAssignFullBranchAccess(true);
    setAssignSelectedBranchIds([]);
    setAssignBranchSearch("");
    setAssignExpandedStores(new Set());
    setAssignStaffSearch("");
    setAssignSubmitting(false);
    setStaffCandidates([]);
    setLoadingStaff(false);
    setAssignLoadingStores(false);
  }

  function closeAssignModal() {
    setPickUserOpen(false);
    resetAssignWizard();
  }

  const filteredAssignStores = useMemo(() => {
    return assignStoresList.filter((s) => {
      if (!assignBranchSearch.trim()) return true;
      const q = assignBranchSearch.trim().toLowerCase();
      if (String(s.name || "").toLowerCase().includes(q)) return true;
      return (s.branches || []).some((b) => String(b.name || "").toLowerCase().includes(q));
    });
  }, [assignStoresList, assignBranchSearch]);

  const filteredStaffCandidates = useMemo(() => {
    const q = assignStaffSearch.trim().toLowerCase();
    if (!q) return staffCandidates;
    return staffCandidates.filter((u) => {
      const un = String(u.username || "").toLowerCase();
      const em = String(u.email || "").toLowerCase();
      return un.includes(q) || em.includes(q);
    });
  }, [staffCandidates, assignStaffSearch]);

  async function openPickUser() {
    if (!canUpdate) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    if (!roleId) return;
    if (!rid && !bypass) return;

    resetAssignWizard();
    setPickUserOpen(true);
    setLoadingStaff(true);
    setAssignLoadingStores(true);

    try {
      const storesRes = await api.get("/stores/my");
      const list = storesRes.data?.stores || [];
      const roleStoreId = normalizeRestaurantId(roleMeta?.restaurantId);
      const scopedStores = roleStoreId ? list.filter((s) => String(s._id) === roleStoreId) : list;

      if (scopedStores.length === 0) {
        toast.error(t("rbac.noStoresForAssignments"));
        closeAssignModal();
        return;
      }
      setAssignStoresList(scopedStores);

      const staffParams = new URLSearchParams();
      staffParams.set("limit", "100");
      const tenantForStaff = rid || roleStoreId || assignmentTenantId;
      if (!bypass) {
        if (!tenantForStaff) {
          toast.error(t("rbac.restaurantContextMissing"));
          closeAssignModal();
          return;
        }
        staffParams.set("restaurantId", tenantForStaff);
      } else if (tenantForStaff) {
        staffParams.set("restaurantId", tenantForStaff);
      }

      const staffRes = await api.get(`/v1/staff-users?${staffParams}`);
      const rows = staffRes.data?.data || [];

      const accountLevelPick = bypass && !tenantForStaff;
      let candidates = rows;
      if (!accountLevelPick && tenantForStaff) {
        const assignParams = new URLSearchParams();
        assignParams.set("roleId", roleId);
        assignParams.set("restaurantId", tenantForStaff);
        const assignRes = await api.get(`/v1/user-role-assignments?${assignParams}`);
        const assignedRows = Array.isArray(assignRes.data) ? assignRes.data : [];
        const assignedIds = new Set(assignedRows.map((a) => String(a.userId?._id || a.userId)));
        candidates = rows.filter((u) => !assignedIds.has(String(u._id)));
      }

      setStaffCandidates(candidates);
    } catch {
      toast.error(t("rbac.failedLoadUsers"));
      setStaffCandidates([]);
      closeAssignModal();
    } finally {
      setLoadingStaff(false);
      setAssignLoadingStores(false);
    }
  }

  function toggleAssignBranch(branchId) {
    const id = String(branchId);
    setAssignSelectedBranchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function toggleAssignAllBranchesInStore(store) {
    const bids = (store.branches || []).map((b) => String(b._id));
    const allOn = bids.length > 0 && bids.every((id) => assignSelectedBranchIds.includes(id));
    setAssignSelectedBranchIds((prev) => {
      if (allOn) return prev.filter((id) => !bids.includes(id));
      const set = new Set(prev);
      bids.forEach((id) => set.add(id));
      return [...set];
    });
  }

  function toggleAssignSelectEveryBranch() {
    const all = getAllBranchIds(assignStoresList);
    const allSelected = all.length > 0 && all.every((id) => assignSelectedBranchIds.includes(id));
    setAssignSelectedBranchIds(allSelected ? [] : all);
  }

  async function submitAssignToRole() {
    if (!canUpdate) {
      toast.error(t("noPermissionToAccessThis"));
      return;
    }
    if (!roleId || !selectedAssignUser?._id) return;

    const stores = assignStoresList;
    const roleStoreId = normalizeRestaurantId(roleMeta?.restaurantId);
    const singleTenantRid = rid || roleStoreId;

    if (!assignFullBranchAccess) {
      const allBranches = getAllBranchIds(stores);
      if (allBranches.length > 0 && assignSelectedBranchIds.length === 0) {
        toast.warn(t("rbac.selectBranchOrFullAccess"));
        return;
      }
    }

    const storesForAssignment = [];
    if (bypass && stores.length > 0) {
      if (assignFullBranchAccess) {
        storesForAssignment.push(...stores.map((s) => s._id));
      } else if (assignSelectedBranchIds.length > 0) {
        const uniqueStoreIds = new Set();
        stores.forEach((store) => {
          if (store.branches?.some((branch) => assignSelectedBranchIds.includes(String(branch._id)))) {
            uniqueStoreIds.add(store._id);
          }
        });
        storesForAssignment.push(...Array.from(uniqueStoreIds));
      }
    } else if (singleTenantRid) {
      storesForAssignment.push(singleTenantRid);
    }

    if (storesForAssignment.length === 0) {
      toast.error(
        t("rbac.restaurantContextRequiredForAssignments")
      );
      return;
    }

    setAssignSubmitting(true);
    try {
      await Promise.all(
        storesForAssignment.map(async (storeId) => {
          let storeScope;
          if (assignFullBranchAccess || singleTenantRid) {
            storeScope = { scopeType: "restaurant", scopeIds: [] };
          } else {
            const store = stores.find((s) => String(s._id) === String(storeId));
            const storeBranchIds =
              store?.branches
                ?.filter((branch) => assignSelectedBranchIds.includes(String(branch._id)))
                .map((branch) => branch._id) || [];

            if (
              storeBranchIds.length === (store?.branches?.length || 0) &&
              storeBranchIds.length > 0
            ) {
              storeScope = { scopeType: "store", scopeIds: [storeId] };
            } else {
              storeScope = { scopeType: "branch", scopeIds: storeBranchIds };
            }
          }

          return api.post("/v1/user-role-assignments", {
            restaurantId: storeId,
            userId: selectedAssignUser._id,
            roleId,
            scopeType: storeScope.scopeType,
            scopeIds: storeScope.scopeIds,
          });
        })
      );
      toast.success(t("rbac.userAssigned"));
      closeAssignModal();
      fetchAssignments();
      onSaved?.();
    } catch (err) {
      toast.error(translateRbacApiMessage(err.response?.data?.message, t) || t("rbac.assignFailed"));
    } finally {
      setAssignSubmitting(false);
    }
  }

  const footerPrimaryLabel = isEdit ? t("rbac.saveChanges") : t("rbac.saveRole");

  return (
    <>
      <Modal
        show={show && !pickUserOpen}
        onHide={() => !saving && onHide()}
        size="lg"
        centered
        scrollable
        dialogClassName="role-wizard-modal"
        contentClassName="border-0 rounded-3 shadow-sm bg-white"
      >
        <style>{ROLE_WIZARD_MODAL_STYLES}</style>
        <Modal.Header closeButton={false} className="border-0 align-items-start">
          <div className="w-100 pe-2">
            {!isEdit ? (
              <>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <Modal.Title className="text-dark mb-0 flex-grow-1 min-w-0" style={{ fontWeight: 700 }}>
                    {t("rbac.createNewRole")}
                  </Modal.Title>
                  <CloseButton
                    className="flex-shrink-0 m-0"
                    aria-label={t("close")}
                    disabled={saving}
                    onClick={() => !saving && onHide()}
                  />
                </div>
                <p className="role-wizard-subtitle mb-0">
                  {t("rbac.defineRoleSubtitle")}
                </p>
              </>
            ) : (
              <>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1 min-w-0">
                    <Modal.Title className="text-dark mb-0" style={{ fontWeight: 700 }}>
                      {name || t("Role")}
                    </Modal.Title>
                    <Badge pill className="role-wizard-user-count-badge">
                      {assignedCount}{" "}
                      {assignedCount === 1 ? t("user") : t("users")}
                    </Badge>
                  </div>
                  <CloseButton
                    className="flex-shrink-0 m-0"
                    aria-label={t("close")}
                    disabled={saving}
                    onClick={() => !saving && onHide()}
                  />
                </div>
                {lastEditedLine && (
                  <p className="role-wizard-subtitle mb-0">{lastEditedLine}</p>
                )}
              </>
            )}
          </div>
        </Modal.Header>

        <Modal.Body dir={uiDir} style={{ fontFamily: "Inter, sans-serif" }} className="onehub-brand-form-controls">
          <style>{ONEHUB_BRAND_FORM_CONTROLS_CSS}</style>
          <style>{ROLE_WIZARD_NAV_STYLES}</style>
          {loadingRole ? (
            <div className="text-center py-5">
              <MDBSpinner />
            </div>
          ) : (
            <>
              {isEdit && (
                <Nav
                  className="role-wizard-nav role-wizard-nav-segmented mb-0 nav"
                  dir={uiDir}
                  activeKey={activeTab}
                  onSelect={(k) => {
                    if (!k) return;
                    setActiveTab(k);
                    if (k === "details") closeAssignModal();
                  }}
                  role="tablist"
                  aria-label={t("rbac.roleWizardTabsAria", {
                    defaultValue: "Switch between role details and assigned users",
                  })}
                >
                  <Nav.Item>
                    <Nav.Link
                      className="role-wizard-nav-link"
                      eventKey="details"
                      role="tab"
                      aria-selected={activeTab === "details"}
                    >
                      {t("rbac.roleDetailsPermissionTab")}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      className="role-wizard-nav-link"
                      eventKey="users"
                      role="tab"
                      aria-selected={activeTab === "users"}
                    >
                      {t("rbac.assignedUsersTab")}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              )}

              {(activeTab === "details" || !isEdit) && (
                <>
                  <div className="role-wizard-details-block">
                    <div className="role-wizard-section-label">
                      {t("rbac.roleDetailsSectionHeading", { defaultValue: "Role details" })}
                    </div>
                    <Form.Label className="role-wizard-field-label d-block">{t("rbac.roleNameLabel")}</Form.Label>
                    <Form.Control
                      className="role-wizard-input"
                      value={name}
                      readOnly={mutationLocked}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (fieldErrors.name) {
                          setFieldErrors((prev) => ({ ...prev, name: undefined }));
                        }
                      }}
                      placeholder={t("rbac.roleNamePlaceholderManager")}
                    />
                    {fieldErrors.name ? (
                      <p className="role-wizard-field-error text-danger">{fieldErrors.name}</p>
                    ) : null}
                    <Form.Label className="role-wizard-field-label d-block role-wizard-description-label">
                      {t("Description")} ({t("rbac.optionalLower")})
                    </Form.Label>
                    <Form.Control
                      className="role-wizard-input"
                      as="textarea"
                      rows={1}
                      style={{ resize: "vertical", minHeight: 42 }}
                      value={description}
                      readOnly={mutationLocked}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("rbac.describeRolePlaceholder")}
                    />
                  </div>

                  {isProtectedAdmin ? (
                    <div className="alert alert-secondary small mb-3 py-2" role="status">
                      {t("rbac.adminRoleReadOnly")}
                    </div>
                  ) : isProtectedDefaultRole ? (
                    <div className="alert alert-secondary small mb-3 py-2" role="status">
                      {t("rbac.defaultRoleReadOnly")}
                    </div>
                  ) : null}

                  {!isProtectedRole && usersMutateWithoutRoleDeps ? (
                    <div className="alert alert-warning small mb-2 py-2" role="status">
                      {t("rbac.usersMgmtInvalidComboBanner")}
                    </div>
                  ) : null}

                  <div className="role-wizard-permissions-block">
                    <PermissionAccordion
                      domains={permDomains.length ? permDomains : null}
                      registry={registry}
                      selected={permissions}
                      onChangeSelected={(rawNext) => {
                        if (mutationLocked) return;
                        const { permissions: fixed, toastKey } = reconcileUsersMutateRequiresRolesView(
                          permissions,
                          rawNext
                        );
                        if (toastKey === "autoAddedDeps") {
                          toast.info(t("rbac.usersMgmtAutoAddedRolesViewAndCreate"));
                          const sectionId =
                            permissionDomainIdContainingKey(permDomains, ROLES_VIEW_PERM) || "roles";
                          setExpandedMods((prev) => new Set(prev).add(sectionId));
                        } else if (toastKey === "stripped") {
                          toast.info(t("rbac.usersMgmtStrippedNeedsRolesView"));
                        }
                        setPermissions(fixed);
                        if (fieldErrors.permissions) {
                          setFieldErrors((prev) => ({ ...prev, permissions: undefined }));
                        }
                      }}
                      expanded={expandedMods}
                      onToggleExpand={toggleExpand}
                      disabled={mutationLocked}
                      dir={uiDir}
                      t={t}
                    />
                  </div>
                  {fieldErrors.permissions ? (
                    <p className="role-wizard-field-error text-danger mt-1">{fieldErrors.permissions}</p>
                  ) : null}

                  {isEdit && !isProtectedRole && (
                    <div className="role-wizard-delete-role-panel d-flex align-items-center justify-content-between gap-3 flex-wrap">
                      <div className="role-wizard-delete-role-copy flex-grow-1 min-w-0">
                        <div className="role-wizard-delete-role-title">{t("rbac.deleteThisRoleHeading")}</div>
                        <p className="role-wizard-delete-role-desc">
                          {assignedCount > 0
                            ? t("rbac.deleteRoleWarningCount", { count: assignedCount })
                            : t("rbac.noUsersAssignedThisRole")}
                        </p>
                      </div>
                      <div className="role-wizard-delete-role-actions flex-shrink-0">
                        {canDelete ? (
                          <Button
                            outline
                            className="m-0 btn-role-wizard-delete-outline"
                            onClick={openDeleteRoleConfirm}
                          >
                            {t("rbac.deleteRoleButton")}
                          </Button>
                        ) : (
                          <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                            <span className="d-inline-block">
                              <Button
                                outline
                                className="m-0 btn-role-wizard-delete-outline"
                                disabled
                                onClick={openDeleteRoleConfirm}
                              >
                                {t("rbac.deleteRoleButton")}
                              </Button>
                            </span>
                          </MDBTooltip>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}

              {isEdit && activeTab === "users" && (
                <div className="role-wizard-assigned-users">
                  <div className="role-wizard-assigned-users__head">
                    <span className="role-wizard-assigned-users__label">
                      {t("rbac.usersAssignedToRole", { count: uniqueUserCount })}
                    </span>
                    {canUpdate && !isProtectedRole ? (
                      <button
                        type="button"
                        className="role-wizard-assign-user-btn"
                        onClick={openPickUser}
                        disabled={saving || loadingAssignments}
                      >
                        <span className="role-wizard-assign-user-btn__plus" aria-hidden="true">
                          +
                        </span>
                        {t("rbac.assignUser")}
                      </button>
                    ) : null}
                  </div>

                  {loadingAssignments ? (
                    <div className="text-center py-4">
                      <MDBSpinner />
                    </div>
                  ) : assignments.length === 0 ? (
                    <p className="role-wizard-assigned-users__empty">{t("rbac.noUsersAssignedYet")}</p>
                  ) : (
                    <div className="role-wizard-assigned-users__list">
                      {Array.from(assignmentsByUser.entries()).map(([uid, rowsForUser]) => {
                        const row = rowsForUser[0];
                        const u = row.userId;
                        const userObj = typeof u === "object" && u ? u : null;
                        const fullName = displayFullName(userObj, t("rbac.userLabel"));
                        const username = String(userObj?.username || "").trim();
                        return (
                          <div key={uid} className="role-wizard-assigned-user-row">
                            <div className="role-wizard-assigned-user-avatar" aria-hidden="true">
                              {initials(userObj)}
                            </div>
                            <div className="role-wizard-assigned-user-text">
                              <div className="role-wizard-assigned-user-name">{fullName}</div>
                              {username ? (
                                <div className="role-wizard-assigned-user-username">{username}</div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          className="border-0 d-flex justify-content-between align-items-center w-100 flex-nowrap gap-3"
          dir={uiDir}
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <Button outline className="m-0 btn-role-wizard-cancel" onClick={onHide} disabled={saving}>
            {t("Cancel")}
          </Button>
          <div className="d-flex align-items-center gap-2" style={{ marginInlineStart: "auto" }}>
            {(activeTab === "details" || !isEdit) && showDetailsSaveButton &&
              (isProtectedRole ? (
                <MDBTooltip tag="span" title={protectedRoleSaveTip}>
                  <span className="d-inline-block">
                    <Button className="m-0 btn-role-wizard-primary" disabled>
                      {footerPrimaryLabel}
                    </Button>
                  </span>
                </MDBTooltip>
              ) : (
                <Button
                  className="m-0 btn-role-wizard-primary"
                  onClick={handleSave}
                  disabled={saving || loadingRole}
                >
                  {saving ? <MDBSpinner size="sm" color="light" /> : footerPrimaryLabel}
                </Button>
              ))}
            {isEdit && activeTab === "users" && (
              <Button
                className="m-0 btn-role-wizard-primary"
                onClick={onHide}
                disabled={saving || isProtectedRole}
              >
                {t("rbac.saveChanges")}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        show={show && pickUserOpen}
        onHide={() => !assignSubmitting && closeAssignModal()}
        size="lg"
        centered
        scrollable
        backdrop="static"
      >
        <Modal.Header
          closeButton={false}
          className="border-0 pb-0"
          style={{ fontFamily: "Inter, sans-serif", padding: "20px 24px 0" }}
        >
          <div className="w-100">
            <button
              type="button"
              className="btn btn-link p-0 mb-2 small text-decoration-none text-start"
              style={{ color: BRAND }}
              disabled={assignSubmitting}
              onClick={() => {
                closeAssignModal();
                setActiveTab("details");
              }}
            >
              {t("rbac.roleDetailsPermissionTab")}
            </button>
            <div className="d-flex align-items-center justify-content-between gap-2">
              <Modal.Title className="text-dark mb-0 flex-grow-1 min-w-0" style={{ fontWeight: 700 }}>
                {assignWizardStep === 0 ? t("rbac.assignUser") : t("rbac.brandAndBranch")}
              </Modal.Title>
              <CloseButton
                className="flex-shrink-0 m-0"
                aria-label={t("close")}
                disabled={assignSubmitting}
                onClick={() => !assignSubmitting && closeAssignModal()}
              />
            </div>
            <div className="text-muted small mt-1">
              {t("rbac.stepCurrentOfTotal", { current: assignWizardStep + 1, total: 2 })}
              {assignWizardStep === 0
                ? ` · ${t("rbac.chooseTeamMember")}`
                : ` · ${selectedAssignUser?.username || ""}`}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body
          dir={uiDir}
          style={{ maxHeight: "min(70vh, 520px)", overflowY: "auto", fontFamily: "Inter, sans-serif", padding: "24px 24px" }}
          className="onehub-brand-form-controls"
        >
          <style>{ONEHUB_BRAND_FORM_CONTROLS_CSS}</style>
          {assignWizardStep === 0 && (
            <div>
              <p className="text-muted small mb-3">
                {t("rbac.pickUserHint")}
              </p>
              <MDBInput
                wrapperClass="mb-3"
                placeholder={t("rbac.searchNameOrEmail")}
                value={assignStaffSearch}
                onChange={(e) => setAssignStaffSearch(e.target.value)}
              />
              {loadingStaff || assignLoadingStores ? (
                <div className="text-center py-4">
                  <MDBSpinner />
                </div>
              ) : filteredStaffCandidates.length === 0 ? (
                <p className="text-muted small mb-0">{t("rbac.noStaffUsersToAssign")}</p>
              ) : (
                filteredStaffCandidates.map((u) => {
                  const sel = selectedAssignUser && String(selectedAssignUser._id) === String(u._id);
                  return (
                    <button
                      key={u._id}
                      type="button"
                      className="w-100 text-start btn border mb-2 d-flex align-items-center justify-content-between"
                      style={{
                        borderColor: sel ? BRAND : "#dee2e6",
                        background: sel ? "#e8f0f2" : "#fff",
                      }}
                      onClick={() => setSelectedAssignUser(u)}
                    >
                      <span>
                        <span className="fw-semibold d-block">{u.username}</span>
                        <span className="small text-muted">{u.email}</span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          )}

          {assignWizardStep === 1 && (
            <div>
              <p className="text-muted small mb-3">{t("addUserWizard.sellerAccountsHint")}</p>
              <Form.Check
                type="checkbox"
                id="assign-full-access-branches"
                className="mb-3 fw-semibold"
                label={t("rbac.fullAccessAllBrandsBranches")}
                checked={assignFullBranchAccess}
                onChange={(e) => {
                  setAssignFullBranchAccess(e.target.checked);
                  if (e.target.checked) setAssignSelectedBranchIds([]);
                }}
              />

              {!assignFullBranchAccess && (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <MDBInput
                      wrapperClass="flex-grow-1"
                      style={{ minWidth: 200 }}
                      placeholder={t("rbac.searchBrandsOrBranches")}
                      value={assignBranchSearch}
                      onChange={(e) => setAssignBranchSearch(e.target.value)}
                    />
                    <Button
                      size="sm"
                      outline
                      style={{ color: BRAND, borderColor: BRAND }}
                      type="button"
                      onClick={toggleAssignSelectEveryBranch}
                    >
                      {t("rbac.selectAllBranches")}
                    </Button>
                  </div>
                  <div className="border rounded-3 p-2" style={{ maxHeight: 320, overflowY: "auto" }}>
                    {filteredAssignStores.map((store) => {
                      const branches = store.branches || [];
                      const expanded = assignExpandedStores.has(store._id);
                      const bids = branches.map((b) => String(b._id));
                      const numSel = bids.filter((id) => assignSelectedBranchIds.includes(id)).length;
                      const storeTitle = `${store.name}${branches.length ? ` (${numSel}/${branches.length})` : ""}`;
                      return (
                        <div key={store._id} className="border-bottom py-2">
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-dark"
                              onClick={() =>
                                setAssignExpandedStores((prev) => {
                                  const n = new Set(prev);
                                  if (n.has(store._id)) n.delete(store._id);
                                  else n.add(store._id);
                                  return n;
                                })
                              }
                            >
                              <ChevronRight
                                size={18}
                                style={{
                                  transform: expanded ? "rotate(90deg)" : "none",
                                  transition: "transform 0.2s",
                                }}
                              />
                            </button>
                            <Form.Check
                              type="checkbox"
                              id={`assign-store-all-${store._id}`}
                              label={<span className="fw-semibold">{storeTitle}</span>}
                              checked={branches.length > 0 && numSel === branches.length}
                              ref={(el) => {
                                if (el && branches.length)
                                  el.indeterminate = numSel > 0 && numSel < branches.length;
                              }}
                              onChange={() => toggleAssignAllBranchesInStore(store)}
                              disabled={branches.length === 0}
                            />
                          </div>
                          {expanded &&
                            branches.length > 0 &&
                            branches.map((b) => (
                              <div key={b._id} className="ps-5 pt-1">
                                <Form.Check
                                  type="checkbox"
                                  className="small mb-1"
                                  label={b.name || String(b._id)}
                                  checked={assignSelectedBranchIds.includes(String(b._id))}
                                  onChange={() => toggleAssignBranch(b._id)}
                                />
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          className="border-0 pt-0 d-flex justify-content-between align-items-center w-100 flex-wrap gap-2"
          dir={uiDir}
          style={{ fontFamily: "Inter, sans-serif", padding: "0 24px 20px" }}
        >
          <Button outline color="secondary" className="m-0" onClick={closeAssignModal} disabled={assignSubmitting}>
            {t("Cancel")}
          </Button>
          <div className="d-flex align-items-center gap-2" style={{ marginInlineStart: "auto" }}>
            {assignWizardStep === 1 ? (
              <>
                <Button
                  outline
                  className="m-0"
                  style={{ borderColor: BRAND, color: BRAND }}
                  onClick={() => setAssignWizardStep(0)}
                  disabled={assignSubmitting}
                >
                  {t("rbac.back")}
                </Button>
                <Button
                  className="m-0"
                  style={{ backgroundColor: BRAND, color: "#fff" }}
                  onClick={submitAssignToRole}
                  disabled={assignSubmitting}
                >
                  {assignSubmitting ? <MDBSpinner size="sm" color="light" /> : t("rbac.assignUser")}
                </Button>
              </>
            ) : (
              <Button
                className="m-0"
                style={{ backgroundColor: BRAND, color: "#fff" }}
                disabled={!selectedAssignUser || loadingStaff}
                onClick={() => setAssignWizardStep(1)}
              >
                {t("rbac.continue")}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={confirmData.title}
        message={confirmData.message}
        action={confirmData.action}
        actionData={null}
        visibility={null}
      />
    </>
  );
}
